import React from 'react';

import { css } from '@emotion/core';
import styled from '@emotion/styled';

import {
  BREAKPOINTS,
  disabledColor,
  getColWidthCalc,
  unit,
} from '@zego/theme';

import UploadedImage from '@zego/components/UploadedImage';

const MOBILE_BASELINE = unit(4);
const DESKTOP_BASELINE = unit(4);
const BASELINE = DESKTOP_BASELINE;

const defaultWidth = css`
  margin: 0 auto;
  ${BREAKPOINTS.medium} {
    width: ${getColWidthCalc(12, 20, 8)};
  }
`;

const defaultBlock = css`
  ${defaultWidth};
  margin-bottom: ${MOBILE_BASELINE}px;
  ${BREAKPOINTS.medium} {
    margin-bottom: ${DESKTOP_BASELINE}px;
  }
`;

const defaultType = css`
  ${defaultBlock};
  color: ${disabledColor(10)};
  font-size: 16px;
  line-height: ${MOBILE_BASELINE}px;
  ${BREAKPOINTS.medium} {
    font-size: 18px;
    line-height: ${DESKTOP_BASELINE}px;
  }
`;

export const Paragraph = styled.p`
  ${defaultType};
`;

export const Image = styled(UploadedImage)`
  ${defaultBlock};
  position: relative;
  max-width: '100vw';
  margin-left: -30px;
  margin-right: -30px;
  display: block;
  ${BREAKPOINTS.medium} {
    padding-top: 8px;
    width: ${({ display }) => {
      switch (display) {
        case 'full':
          return '100vw';
        case 'wide':
          return getColWidthCalc(12, 20, 10);
        case 'inline-left':
        case 'inline-right':
          return getColWidthCalc(12, 20, 6);
        default:
          return getColWidthCalc(12, 20, 8);
      }
    }};
    left: ${({ display }) => (display === 'full' ? '50%' : '0')};
    right: ${({ display }) => (display === 'full' ? '50%' : '0')};
    margin-right: ${({ display }) =>
      display === 'inline-left'
        ? `${DESKTOP_BASELINE}px`
        : display === 'full'
        ? '-50vw'
        : 'auto'};
    margin-left: ${({ display }) =>
      display === 'inline-right'
        ? `${DESKTOP_BASELINE}px`
        : display === 'full'
        ? '-50vw'
        : 'auto'};
    float: ${({ display }) => {
      switch (display) {
        case 'inline-left':
          return 'left';
        case 'inline-right':
          return 'right';
        default:
          return 'none';
      }
    }};
  }
`;

export const Blockquote = styled.blockquote`
  ${defaultType};
  font-style: italic;
  border-left: 4px solid ${disabledColor(5)};
  padding-left: ${unit(4) - 4}px;
  ${BREAKPOINTS.medium} {
    position: relative;
    left: -${unit(4)}px;
  }
`;

export const Header = styled.h2`
  ${defaultType};
  font-size: 20px;
  font-weight: bold;
  margin-top: ${2 * BASELINE}px;
  ${BREAKPOINTS.medium} {
    font-size: 22px;
  }
`;

export const ListItem = styled.li`
  margin: 0 0 0 ${BASELINE}px;
  padding-left: ${unit(2)}px;
`;

export const UnorderedList = styled.ul`
  ${defaultType};
  padding: 0;
  ${ListItem} {
    list-style-type: disc;
  }
`;

export const OrderedList = styled.ol`
  ${defaultType};
  padding: 0;
  ${ListItem} {
    list-style-type: decimal;
  }
`;

export const IFrameWrapper = ({ title, videoID }) => {
  return (
    <div
      css={css`
        position: relative;
        height: 0;
        padding-top: 56.25%;
        margin: 20px auto;
      `}
    >
      <iframe
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          right: 0;
          width: 100%;
          height: 100%;
        `}
        title={title}
        src={`https://www.youtube.com/embed/${videoID}?controls=0`}
        frameBorder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};
